import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';

import { logger } from '../utils/logger';
import { useAuth } from '../hooks/useAuth';
import {
  useChat,
  ChatMessage,
  type ChatSendResult,
  type WelcomeMessageKey,
} from '../hooks/useChat';
import { useConversationSuggestions } from '../hooks/useConversationSuggestions';
import { useSessionKeepAlive } from '../hooks/useSessionKeepAlive';
import { useScrollRestore } from '../hooks/useScrollRestore';
import { useMessageFeedback } from '../hooks/useMessageFeedback';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { FeedbackModal } from './FeedbackModal';
import { WelcomePanel } from './WelcomePanel';
import { CustomMessageList, CustomMessageListHandle } from './CustomMessageList';
import { MessageInput } from './MessageInput';
import { MessageItem } from './MessageItem';
import { ThemedSpinner } from './ThemedSpinner';
import { useTranslation } from '../localization/useAppTranslation';
import { usePromptSuggestions } from '../context/PromptSuggestionsContext';
import { chatService } from '../services/chatService';
import { pairLiveAssistantResponsesWithSourcePrompts } from '../utils/promptSourcePairing';
import type { SavedPromptFromChatRequest } from '../types/preferences';
import type { ConversationSuggestion } from '../types/conversationSuggestions';
import type { PromptSuggestionItem } from '../types/promptSuggestions';
import { DEFAULT_RESPONSE_LOCALE } from '../localization/locales';
import '../styles/CustomMessages.css';

const moduleLogger = logger.getModule('ChatContainer');
const DEFAULT_WELCOME_MESSAGE_KEY: WelcomeMessageKey = 'welcome.newUser';
const WELCOME_CARD_COUNT = 3;
const PROMPT_SUGGESTION_EVENT_URL_PREFIX = 'speedgauge://prompt-suggestion';
const CONVERSATION_SUGGESTION_EVENT_URL_PREFIX = 'speedgauge://conversation-suggestion';

const hashWelcomeSuggestionSeed = (seed: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const createWelcomeSuggestionPool = (
  composerSuggestions: PromptSuggestionItem[],
  homeSuggestions: PromptSuggestionItem[],
  sessionId: string | undefined,
): PromptSuggestionItem[] => {
  const suggestionsById = new Map<string, PromptSuggestionItem>();
  for (const suggestion of [...composerSuggestions, ...homeSuggestions]) {
    if (!suggestionsById.has(suggestion.id)) {
      suggestionsById.set(suggestion.id, suggestion);
    }
  }

  const mixedSuggestions = [...suggestionsById.values()];
  let randomState = hashWelcomeSuggestionSeed(
    `${sessionId || 'welcome'}:${mixedSuggestions.map((suggestion) => suggestion.id).join(':')}`,
  );
  for (let index = mixedSuggestions.length - 1; index > 0; index -= 1) {
    randomState = (Math.imul(randomState, 1664525) + 1013904223) >>> 0;
    const swapIndex = Math.floor((randomState / 4294967296) * (index + 1));
    [mixedSuggestions[index], mixedSuggestions[swapIndex]] = [
      mixedSuggestions[swapIndex], mixedSuggestions[index],
    ];
  }

  return mixedSuggestions;
};

interface ChatContainerProps {
  composerDraft: string;
  onComposerDraftChange: (draft: string) => void;
  composerInputRef: React.RefObject<HTMLTextAreaElement>;
  onResponseStateChange?: (isReceivingResponse: boolean) => void;
  onWelcomeStateChange?: (isWelcomeVisible: boolean) => void;
  onGenerateSavedPromptDraft?: (sourcePrompt: SavedPromptFromChatRequest) => Promise<void>;
}

export interface ChatContainerHandle {
  sendCoachSuggestion: (suggestion: PromptSuggestionItem) => boolean;
  sendSavedPrompt: (message: string) => boolean;
}

export const ChatContainer = React.forwardRef<ChatContainerHandle, ChatContainerProps>(({
  composerDraft,
  onComposerDraftChange,
  composerInputRef,
  onResponseStateChange,
  onWelcomeStateChange,
  onGenerateSavedPromptDraft,
}, ref) => {
  const { t } = useTranslation('chat');
  const { session } = useAuth();
  const {
    messages,
    sendMessage,
    updateMessageMetadata,
    isLoading,
    error,
    clearError,
  } = useChat();
  const { isInitialLoadComplete, preferences } = useUserPreferences();
  const {
    error: promptSuggestionsError,
    status: promptSuggestionsStatus,
    suggestionsForSurface,
  } = usePromptSuggestions();
  const {
    feedbackModal,
    openFeedback,
    closeFeedback,
    submitFeedback,
  } = useMessageFeedback({
    activeSessionId: session?.sessionId,
    targetSessionId: session?.sessionId,
  });
  const messageListRef = useRef<CustomMessageListHandle | null>(null);
  const isPromptSendInProgressRef = useRef(false);
  const isResponseLoadingRef = useRef(isLoading);
  const [pendingSuggestionSources, setPendingSuggestionSources] = useState<Array<{
    sessionId: string;
    turnNumber: number;
    assistantMessageId: string;
  }>>([]);
  const [pendingSuggestionMessageIds, setPendingSuggestionMessageIds] = useState<Set<string>>(
    () => new Set(),
  );
  const responseLocale = preferences?.response_locale ?? DEFAULT_RESPONSE_LOCALE;
  const {
    requestSuggestions,
  } = useConversationSuggestions({
    sessionId: session?.sessionId,
    responseLocale,
  });
  const showTypingIndicator = isLoading;
  isResponseLoadingRef.current = isLoading;

  useEffect(() => {
    onResponseStateChange?.(isLoading);
  }, [isLoading, onResponseStateChange]);

  useEffect(() => {
    setPendingSuggestionSources([]);
    setPendingSuggestionMessageIds(new Set());
  }, [responseLocale, session?.sessionId]);

  useEffect(() => {
    if (
      !isInitialLoadComplete
      || !session?.sessionId
      || pendingSuggestionSources.length === 0
    ) {
      return;
    }

    const sourcesForActiveSession = pendingSuggestionSources.filter(
      (source) => source.sessionId === session.sessionId,
    );
    setPendingSuggestionSources([]);

    sourcesForActiveSession.forEach((source) => {
      requestSuggestions(source.turnNumber, (suggestions) => {
        updateMessageMetadata(source.assistantMessageId, {
          conversationSuggestions: suggestions,
          conversationSuggestionLocale: responseLocale,
        });
        setPendingSuggestionMessageIds((currentIds) => {
          const nextIds = new Set(currentIds);
          nextIds.delete(source.assistantMessageId);
          return nextIds;
        });
      }, () => {
        setPendingSuggestionMessageIds((currentIds) => {
          const nextIds = new Set(currentIds);
          nextIds.delete(source.assistantMessageId);
          return nextIds;
        });
      });
    });
  }, [
    isInitialLoadComplete,
    pendingSuggestionSources,
    requestSuggestions,
    responseLocale,
    session?.sessionId,
    updateMessageMetadata,
  ]);

  // Restore scroll position on refresh (pass messages to trigger restore when they load)
  // Cast to allow both HTMLDivElement and CustomMessageListHandle types
  useScrollRestore('chat-scroll-position', messageListRef as React.RefObject<HTMLDivElement>, messages);

  // Keep session alive while chatting
  useSessionKeepAlive(session?.sessionId, {
    intervalMs: 5 * 60 * 1000, // Every 5 minutes during active chat
    enableVisibilityTracking: true // Refresh when user returns to window
  });

  // Focus input when new messages arrive
  React.useEffect(() => {
    if (composerInputRef.current) {
      composerInputRef.current.focus();
    }
  }, [composerInputRef, messages]);

  const handleSend = useCallback(async (message: string): Promise<ChatSendResult | null> => {
    if (!message.trim()) {
      return null;
    }

    onComposerDraftChange('');
    try {
      const sendResult = await sendMessage(message);
      if (sendResult && session?.sessionId) {
        setPendingSuggestionMessageIds((currentIds) => new Set([
          ...currentIds,
          sendResult.assistantMessageId,
        ]));
        setPendingSuggestionSources((currentSources) => [...currentSources, {
          sessionId: session.sessionId,
          turnNumber: sendResult.sourceTurnNumber,
          assistantMessageId: sendResult.assistantMessageId,
        }]);
      }
      return sendResult;
    } catch (error) {
      moduleLogger.error('Failed to send message', error);
      return null;
    }
  }, [onComposerDraftChange, sendMessage, session?.sessionId]);

  const sendQueuedPrompt = useCallback((message: string): boolean => {
    if (!message.trim() || isLoading || isPromptSendInProgressRef.current) {
      return false;
    }

    isPromptSendInProgressRef.current = true;
    void handleSend(message).finally(() => {
      isPromptSendInProgressRef.current = false;
    });
    return true;
  }, [handleSend, isLoading]);
  const sendQueuedPromptRef = useRef(sendQueuedPrompt);
  sendQueuedPromptRef.current = sendQueuedPrompt;

  const sendSavedPrompt = useCallback((message: string): boolean => (
    sendQueuedPrompt(message)
  ), [sendQueuedPrompt]);

  const sendCoachSuggestion = useCallback((suggestion: PromptSuggestionItem): boolean => {
    const didSendSuggestion = sendQueuedPrompt(suggestion.action.prompt_text);
    if (didSendSuggestion && session?.sessionId) {
      void chatService.trackLinkClick(session.sessionId, {
        url: `${PROMPT_SUGGESTION_EVENT_URL_PREFIX}/${encodeURIComponent(suggestion.analytics_key)}`,
        text: suggestion.display.title,
        is_external: false,
      });
    }
    return didSendSuggestion;
  }, [sendQueuedPrompt, session?.sessionId]);

  const sendGeneratedSuggestion = useCallback((suggestion: ConversationSuggestion): boolean => {
    const didSendSuggestion = sendQueuedPromptRef.current(suggestion.prompt_text);
    if (didSendSuggestion && session?.sessionId) {
      void chatService.trackLinkClick(session.sessionId, {
        url: `${CONVERSATION_SUGGESTION_EVENT_URL_PREFIX}/${encodeURIComponent(suggestion.id)}`,
        text: suggestion.label,
        is_external: false,
      });
    }
    return didSendSuggestion;
  }, [session?.sessionId]);

  useImperativeHandle(ref, () => ({
    sendCoachSuggestion,
    sendSavedPrompt,
  }), [sendCoachSuggestion, sendSavedPrompt]);

  const handleInputChange = (innerHtml: string, textContent: string) => {
    onComposerDraftChange(textContent);
  };

  const handleConversationSuggestionClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!isResponseLoadingRef.current || !(event.target instanceof Element)) {
      return;
    }

    if (event.target.closest('.coach-generated-suggestion-chips')) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  // Check if we only have the welcome message (no user messages yet)
  const hasOnlyWelcome = messages.length === 1 && messages[0].id === 'welcome-message';
  const welcomeMessageKey = (
    hasOnlyWelcome ? messages[0].metadata?.syntheticMessageKey : undefined
  ) as WelcomeMessageKey | undefined;
  const chatMessages = messages.filter((message) => message.id !== 'welcome-message');
  const composerSuggestions = suggestionsForSurface('composer');
  const homeSuggestions = suggestionsForSurface('home');
  const welcomeSuggestionPool = useMemo(
    () => createWelcomeSuggestionPool(
      composerSuggestions,
      homeSuggestions,
      session?.sessionId,
    ),
    [composerSuggestions, homeSuggestions, session?.sessionId],
  );
  const welcomeCardSuggestions = welcomeSuggestionPool.slice(0, WELCOME_CARD_COUNT);
  const sourcePromptsByAssistantId = useMemo(
    () => pairLiveAssistantResponsesWithSourcePrompts(messages),
    [messages],
  );

  useEffect(() => {
    onWelcomeStateChange?.(hasOnlyWelcome);
  }, [hasOnlyWelcome, onWelcomeStateChange]);

  return (
    <>
      {/* Show unified welcome panel during initial state */}
      {hasOnlyWelcome && (
        <WelcomePanel
          welcomeMessageKey={welcomeMessageKey || DEFAULT_WELCOME_MESSAGE_KEY}
          onSend={handleSend}
          isLoading={isLoading}
          composerDraft={composerDraft}
          homeSuggestions={welcomeCardSuggestions}
          suggestionError={promptSuggestionsError}
          suggestionStatus={promptSuggestionsStatus}
          onComposerDraftChange={onComposerDraftChange}
          onSelectCoachSuggestion={sendCoachSuggestion}
          inputRef={composerInputRef}
        />
      )}

      {/* Show regular chat view once messages exist beyond welcome */}
      {!hasOnlyWelcome && (
        <div
          className={`chat-conversation${isLoading ? ' is-response-loading' : ''}`}
          onClickCapture={handleConversationSuggestionClickCapture}
        >
          <CustomMessageList
            ref={messageListRef}
            messages={messages}
            autoScrollToBottom={false}
            autoScrollToBottomOnMount={true}
          >
            {/* Render all messages except the welcome message (which is in WelcomePanel) */}
            {chatMessages.map((message: ChatMessage, index: number) => {
              const isLastMessage = index === chatMessages.length - 1;

              return (
                <MessageItem
                  key={message.id}
                  message={message}
                  onFeedbackClick={openFeedback}
                  sourcePrompt={sourcePromptsByAssistantId.get(message.id)}
                  onGenerateSavedPromptDraft={onGenerateSavedPromptDraft}
                  containerRef={messageListRef}
                  showInlineTypingIndicator={showTypingIndicator && isLastMessage && message.direction === 'incoming'}
                  responseLocale={responseLocale}
                  conversationSuggestionsLoading={pendingSuggestionMessageIds.has(message.id)}
                  onSelectConversationSuggestion={sendGeneratedSuggestion}
                />
              );
            })}
            {/* Show initializing state while session is loading */}
            {messages.length === 0 && !isLoading && (
              <div className="loading-spinner-message">
                <div className="loading-spinner">
                  <ThemedSpinner size="lg" ariaLabel={t('status.initializingAriaLabel')} />
                  <p>{t('status.initializing')}</p>
                </div>
              </div>
            )}
          </CustomMessageList>
          <MessageInput
            value={composerDraft}
            onChange={handleInputChange}
            onSend={handleSend}
            disabled={isLoading}
            inputRef={composerInputRef}
            className="chat-conversation-input"
          />
        </div>
      )}
      {error && (
        <div className="error-banner">
          <span className="error-icon" aria-hidden="true">
            <FaExclamationTriangle />
          </span>
          <span className="error-text">{error}</span>
          <button 
            className="error-dismiss"
            onClick={clearError}
            aria-label={t('errors.dismiss')}
            type="button"
          >
            <FaTimes aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModal.isOpen}
        onClose={closeFeedback}
        onSubmit={submitFeedback}
        initialRating={feedbackModal.initialRating}
      />
    </>
  );
});

ChatContainer.displayName = 'ChatContainer';
